/*
 * Flattens the SAME treeData that BusinessNetworkTree renders.
 *
 * No demo/hardcoded business data is used here.
 * Every table row comes from LIST_BUSINESS_NETWORKS -> getRootTreeData().
 *
 * Output order mirrors the tree view:
 *   root network
 *     child networks (recursive)
 *       companies (recursive)
 *     direct companies
 */
const getBusinessNetworkTableRows = treeData => {
  const nodes = Object.values(treeData || {});
  const networks = nodes.filter(node => node?.isNetwork);

  const networkById = networks.reduce((result, network) => {
    result[network.networkId] = network;
    return result;
  }, {});

  const networksByParentId = networks.reduce((result, network) => {
    if (network.isRoot) {
      return result;
    }

    const parentId = network.parentNetworkId ?? null;

    if (!result[parentId]) {
      result[parentId] = [];
    }

    result[parentId].push(network);
    return result;
  }, {});

  Object.values(networksByParentId).forEach(items => {
    items.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "sv")
    );
  });

  const getRootNetwork = network => {
    let current = network;
    let root = network;
    const visited = new Set();

    while (
      current?.parentNetworkId &&
      !visited.has(current.networkId)
    ) {
      visited.add(current.networkId);
      const parent = networkById[current.parentNetworkId];

      if (!parent) {
        break;
      }

      root = parent;
      current = parent;
    }

    return root;
  };

  const rows = [];

  const addCompany = ({
    company,
    currentNetwork,
    rootNetwork,
    level,
    parentName,
  }) => {
    if (!company) {
      return;
    }

    const childIds = Array.isArray(company.childCompanyIds)
      ? company.childCompanyIds
      : [];

    rows.push({
      key: `company-${company.memberId}`,
      node: company,
      type: "company",
      name: company.name,
      level,
      rootNetworkId: rootNetwork?.networkId ?? currentNetwork?.networkId ?? null,
      rootNetworkName: rootNetwork?.name || currentNetwork?.name || "—",
      parentName: parentName || currentNetwork?.name || "—",
      childCount: childIds.length,
      companyId: company.companyId ?? null,
      memberId: company.memberId ?? null,
      networkId: company.networkId ?? currentNetwork?.networkId ?? null,
      idLabel: company.companyId ?? "—",
    });

    childIds.forEach(memberId => {
      addCompany({
        company: treeData?.[`c_${memberId}`],
        currentNetwork,
        rootNetwork,
        level: level + 1,
        parentName: company.name,
      });
    });
  };

  const addNetwork = ({
    network,
    rootNetwork,
    level,
    parentName,
    includeSelf = true,
  }) => {
    if (!network) {
      return;
    }

    const effectiveRoot = rootNetwork || getRootNetwork(network) || network;
    const childNetworks = networksByParentId[network.networkId] || [];
    const childCompanyIds = Array.isArray(network.childCompanyIds)
      ? network.childCompanyIds
      : [];

    if (includeSelf) {
      rows.push({
        key: `network-${network.networkId}`,
        node: network,
        type: "network",
        name: network.name,
        level,
        rootNetworkId: effectiveRoot.networkId,
        rootNetworkName: effectiveRoot.name,
        parentName: parentName || "—",
        childCount: childNetworks.length + childCompanyIds.length,
        companyId: null,
        memberId: null,
        networkId: network.networkId,
        idLabel: network.networkId,
      });
    }

    childNetworks.forEach(childNetwork => {
      addNetwork({
        network: childNetwork,
        rootNetwork: effectiveRoot,
        level: level + 1,
        parentName: network.name,
      });
    });

    childCompanyIds.forEach(memberId => {
      addCompany({
        company: treeData?.[`c_${memberId}`],
        currentNetwork: network,
        rootNetwork: effectiveRoot,
        level: level + 1,
        parentName: network.name,
      });
    });
  };

  const rootNetworks = networks.filter(network => network.isRoot);

  // Same root source as the tree view's ParentNetworkHeading.
  rootNetworks.forEach(rootNetwork => {
    addNetwork({
      network: rootNetwork,
      rootNetwork,
      level: 1,
      parentName: "—",
    });
  });

  // Fallback for installations without an explicit root network.
  if (rootNetworks.length === 0) {
    const topLevelNetworks = networksByParentId[null] || [];

    topLevelNetworks.forEach(network => {
      addNetwork({
        network,
        rootNetwork: network,
        level: 1,
        parentName: "—",
      });
    });
  }

  return rows;
};

export default getBusinessNetworkTableRows;
